import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRightLeft, PackageSearch } from "lucide-react";

/**
 * ITER221: al guardar una cotización / nota de pedido, muestra los productos sin
 * stock que SÍ están disponibles con el mismo principio activo de otro
 * laboratorio, para no generar un pedido de compra innecesario.
 */
export function StockAlternativasDialog({ open, sugerencias = [], onReemplazar, onGuardarIgual, onCerrar, saving, permitirGuardarIgual = true, nota }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCerrar?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="alternativas-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#8a5500]">
            <AlertTriangle size={18} />
            Hay stock del mismo producto en otro laboratorio
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[#4a5c53]">
          Los siguientes productos no tienen stock suficiente, pero el mismo principio activo
          está disponible en bodega bajo otra marca o laboratorio. Puede reemplazarlo con un clic
          para evitar generar un pedido de compra innecesario.
        </p>

        <div className="space-y-4">
          {sugerencias.map((s) => (
            <div key={s.linea_idx} className="border border-[#f5c477] bg-[#fffdf8] rounded-md p-3"
              data-testid={`sugerencia-${s.linea_idx}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#15241d]">{s.producto_nombre}</div>
                  <div className="text-[11px] text-[#5c7368]">
                    {s.laboratorio || "Sin laboratorio"}
                    {s.principio_activo ? ` · ${s.principio_activo}` : ""}
                    {s.concentracion ? ` ${s.concentracion}` : ""}
                  </div>
                </div>
                <Badge className="bg-[#fdecea] text-[#7a2514] border border-[#d99584] text-[10px]">
                  Pide {s.cantidad_requerida_unidades} u. · Stock {s.stock_actual_unidades} u.
                </Badge>
              </div>

              <div className="mt-2 space-y-2">
                {(s.alternativas || []).map((a) => (
                  <div key={a.producto_comercial_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded border border-[#d1dbd6] bg-white p-2">
                    <div className="min-w-0 text-xs">
                      <div className="font-semibold text-[#15241d] flex items-center gap-1">
                        <PackageSearch size={13} className="text-[#205c45]" />
                        {a.nombre_comercial}
                      </div>
                      <div className="text-[11px] text-[#5c7368]">
                        {a.laboratorio || "—"}{a.presentacion ? ` · ${a.presentacion}` : ""}
                        {a.numero_lote ? ` · Lote ${a.numero_lote}` : ""}
                        {a.fecha_vencimiento ? ` · Vence ${a.fecha_vencimiento}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right text-xs">
                        <div className={a.cubre_requerido ? "text-[#205c45] font-semibold" : "text-[#8a5500] font-semibold"}>
                          {a.stock_total} u. disponibles
                        </div>
                        <div className="text-[11px] text-[#5c7368]">
                          ${(a.precio_unidad || 0).toFixed(2)} u. · ${(a.precio_caja || 0).toFixed(2)} caja
                        </div>
                      </div>
                      <Button size="sm" className="bg-[#205c45] hover:bg-[#1a4a37]"
                        onClick={() => onReemplazar?.(s.linea_idx, a)}
                        data-testid={`usar-alternativa-${s.linea_idx}-${a.producto_comercial_id}`}>
                        <ArrowRightLeft size={13} className="mr-1" />Usar este
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {nota && (
          <div className="text-xs text-[#7a2514] bg-[#fdecea] border border-[#d99584] rounded-md p-2"
            data-testid="alternativas-nota">{nota}</div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onCerrar} data-testid="alternativas-revisar-btn">
            Revisar el documento
          </Button>
          {permitirGuardarIgual && (
            <Button onClick={onGuardarIgual} disabled={saving} className="bg-[#8a5500] hover:bg-[#6e4400]"
              data-testid="alternativas-guardar-igual-btn">
              {saving ? "Guardando…" : "Guardar de todas formas"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Confirmación al salir de un formulario con cambios sin guardar. */
export function ConfirmSalirDialog({ open, titulo = "Salir sin guardar", mensaje, onConfirmar, onCancelar }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancelar?.()}>
      <DialogContent className="max-w-md" data-testid="confirm-salir-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#7a2514]">
            <AlertTriangle size={18} />{titulo}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4a5c53]">
          {mensaje || "Tiene cambios sin guardar. ¿Está seguro de que desea salir y descartarlos?"}
        </p>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancelar} data-testid="confirm-salir-cancelar">
            Seguir editando
          </Button>
          <Button className="bg-[#7a2514] hover:bg-[#5f1d0f]" onClick={onConfirmar} data-testid="confirm-salir-confirmar">
            Salir sin guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
